import {Stack, Typography} from '@mui/material';
import {TFunction} from 'i18next';
import {useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {translations} from '../../../../../../../../i18n/translations';
import {PlannerItemDeleteData} from '../../body/planner-item';
import {PlannerBaseModal} from '../planner-base-modal';
import {useModalBtns} from '../../../../../../../../components/modal/use-modal-btns';

export type PlannerConfirmItemDeleteModalProps = {
  open: boolean;
  data: PlannerItemDeleteData[];
  onCancel: () => void;
  onConfirm: () => Promise<void>;
};

export function PlannerConfirmItemDeleteModal(props: PlannerConfirmItemDeleteModalProps) {
  const {t} = useTranslation();
  const [loading, setLoading] = useState(false);

  const actionBtns = useModalBtns({
    left: {
      type: 'cancel',
      disabled: loading,
      onClick: props.onCancel,
    },
    right: {
      type: 'confirm',
      onClick: async () => {
        try {
          setLoading(true);
          await props.onConfirm();
        } finally {
          setLoading(false);
        }
      },
      loading: loading,
    },
  });

  const texts = useMemo(() => getTextsByItemType(props.data, t), [props.data, t]);

  return (
    <PlannerBaseModal open={props.open} onClose={props.onCancel} actions={actionBtns}>
      <Stack
        sx={{
          gap: '25px',
          placeContent: 'center',
          alignItems: 'center',
          paddingTop: '40px',
          paddingBottom: '5px',
        }}
      >
        <Typography
          sx={{
            fontSize: '16px',
            fontWeight: '500',
            color: 'gray.main',
          }}
        >
          {texts.title}
        </Typography>
        <Typography
          sx={{
            fontSize: '14px',
            color: 'gray.input',
          }}
        >
          {texts.desc}
        </Typography>
      </Stack>
    </PlannerBaseModal>
  );
}

// --------------------------------------------------------------------------
// --------------------------------------------------------------------------
// --------------------------------------------------------------------------

function getTextsByItemType(
  deleteData: PlannerItemDeleteData[],
  t: TFunction
): {title: string; desc: string} {
  const data = deleteData[0];

  // ----- ACTIVITY -----
  if (data.item.type === 'activity') {
    const multipleUsers = deleteData.length > 1;

    // multiple users
    if (multipleUsers) {
      const activityName = data.item.activitiesType.name;

      return {
        title: t(translations.planner.modal.delete.title.multiple, {name: activityName}),
        desc: t(translations.planner.modal.delete.desc.multiple),
      };
    }
    // single user
    else {
      const userName = data.user.name;
      return {
        title: t(translations.planner.modal.delete.title.single, {name: userName}),
        desc: t(translations.planner.modal.delete.desc.single),
      };
    }
  }

  // ----- ANNIVERSARY -----
  throw Error(`No implementation: '${data.item}'`);
}
